// Tutorial step definitions for the Config Workspace Details form.
//
// Steps are grouped into builder functions so the same fragments power two
// entry points:
//   1. Full walkthroughs launched from the page-level picker (collector / alloy).
//   2. Section-scoped tutorials launched from the (?) icon on each Alloy section.
//
// Each step is { element, action, value?, message, isStaff? }. `isStaff` steps
// are filtered out for non-staff users by the caller. See composables/tutorial.js
// for the supported actions (type, select, checkbox, click, display).

/* -------------------------------------------------------------------------- */
/* Collector                                                                  */
/* -------------------------------------------------------------------------- */

// Full collector-config walkthrough (page-level picker only).
export function collectorSteps() {
  return [
    {
      element: '#config_type',
      action: 'select',
      value: 'collectorconfig',
      message: 'Choose the configuration type'
    },
    {
      element: '#operating_system',
      action: 'select',
      value: 'Windows',
      message: 'Choose the operating system for this binary (Linux, Windows, or Mac).'
    },
    {
      element: '#space_type',
      action: 'select',
      value: 'serverspace',
      message: 'Choose the space type. Serverspace targets servers and infrastructure hosts. It is the default and the only option on Linux. Workspace targets end-user workstations and unlocks workspace-only settings such as the output database plugin.'
    },
    {
      element: '#organization',
      action: 'select',
      value: 1,
      message: 'Select the organization'
    },
    {
      element: '#choose_file',
      action: 'display',
      value: '',
      message: 'Choose collector config for upload.',
      isStaff: true
    },
    {
      element: '#group',
      action: 'select',
      value: 1,
      message: 'Select the group (optional).'
    },
    {
      element: '#agent',
      action: 'select',
      value: 1,
      message: 'Select the agent that will run this configuration (optional).'
    },

    /* System collectors */
    {
      element: '#gatewayping_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Enable gateway ping monitoring'
    },
    {
      element: '#wifi_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Enable WiFi monitoring'
    },
    {
      element: '#activity_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Enable user activity monitoring (controls Content and Disclosed Mode)'
    },
    {
      element: '#process_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Process monitoring, Tracks CPU, memory, and process usage'
    },
    {
      element: '#processor_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Processor, Tracks CPU performance across all cores'
    },
    {
      element: '#logical_disk_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Logical Disk, Monitors disk space, usage, and performance.'
    },
    {
      element: '#physical_disk_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Physical Disk, Monitors read/write activity and disk performance'
    },
    {
      element: '#network_interface_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Network Interface, Measures network traffic and errors on adapters.'
    },
    {
      element: '#system_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'System, Provides overall system statistics such as uptime and queue length'
    },
    {
      element: '#memory_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Memory, Reports on memory usage and faults'
    },
    {
      element: '#paging_file_bool_toggle',
      action: 'checkbox',
      value: true,
      message: 'Paging File, Monitors swap (paging file) usage'
    },

    /* Synthetic transaction */
    {
      element: '#new_transaction',
      action: 'click',
      value: null,
      message: 'Add a new synthetic transaction'
    },
    {
      element: '#protocol',
      action: 'select',
      value: 'tcp',
      message: 'Select the protocol for the synthetic transaction'
    },
    {
      element: '#name',
      action: 'type',
      value: 'TCP Transaction',
      message: 'Enter a name for the synthetic transaction'
    },
    {
      element: '#address',
      action: 'type',
      value: 'google.com:443',
      message: 'Enter the target address and port'
    },
    {
      element: '#save_transaction',
      action: 'click',
      value: null,
      message: 'Save the synthetic transaction'
    },
    {
      element: '#remove_transaction',
      action: 'display',
      value: null,
      message: 'After click the save button you can click Remove to discard the synthetic transaction'
    },

    /* Output configuration */
    {
      element: '#url',
      action: 'type',
      value: 'https://database:8086',
      message: 'Enter the database endpoint URL',
      isStaff: true
    },
    {
      element: '#add_url',
      action: 'click',
      value: true,
      message: 'Add the database URL',
      isStaff: true
    },
    {
      element: '#added_address',
      action: 'display',
      value: true,
      message: 'Added database addresses will appear here',
      isStaff: true
    },
    {
      element: '#database',
      action: 'type',
      value: 'Telegraf',
      message: 'Enter the database name',
      isStaff: true
    },
    {
      element: '#username',
      action: 'type',
      value: 'Telegraf',
      message: 'Enter the database username',
      isStaff: true
    },
    {
      element: '#password',
      action: 'type',
      value: 'FunTimesIncoming',
      message: 'Enter the database password',
      isStaff: true
    },
    {
      element: 'button[type="submit"]',
      action: 'display',
      value: null,
      message: 'Click to create the binary or save your changes.'
    }
  ];
}

/* -------------------------------------------------------------------------- */
/* Alloy - section fragments                                                  */
/* -------------------------------------------------------------------------- */

// Top-of-form fields. Used only by the full Alloy walkthrough; the section
// tutorials assume the config type / OS are already set (the section is only
// visible once they are).
export function alloyIntroSteps({ os = 'windows' } = {}) {
  const osLabel = os === 'linux' ? 'Linux' : 'Windows';
  const steps = [
    {
      element: '#config_type',
      action: 'select',
      value: 'alloyconfig',
      message: 'Choose the Alloy configuration type.'
    },
    {
      element: '#operating_system',
      action: 'select',
      value: osLabel,
      message: 'Choose the operating system for this Alloy config. The available monitoring modules depend on this.'
    }
  ];

  // Space type is only offered on Windows; Linux is locked to serverspace.
  if (os !== 'linux') {
    steps.push({
      element: '#space_type',
      action: 'select',
      value: 'serverspace',
      message: 'Choose the space type. Serverspace targets servers and infrastructure hosts. It is the default and the only option on Linux. Workspace targets end-user workstations and unlocks workspace-only settings such as the output database plugin.'
    });
  }

  steps.push(
    {
      element: '#organization',
      action: 'select',
      value: 1,
      message: 'Select the organisation this config belongs to.'
    },
    {
      element: '#group',
      action: 'select',
      value: 1,
      message: 'Select the agent group (optional).'
    },
    {
      element: '#agent',
      action: 'select',
      value: 1,
      message: 'Select the agent that will run this configuration (optional).'
    }
  );

  return steps;
}

// Ingest (OTLP) - staff only.
export function alloyOtlpSteps() {
  return [
    {
      element: '#otlp_endpoint',
      action: 'type',
      value: 'https://ingest.mivu.io',
      message: 'The OTLP ingest endpoint Alloy sends metrics and logs to.',
      isStaff: true
    },
    {
      element: '#remote_write_org_id',
      action: 'type',
      value: 'dev',
      message: 'The X-Scope-OrgID tenant header sent with remote-write requests.',
      isStaff: true
    },
    {
      element: '#scrape_interval_seconds',
      action: 'type',
      value: '60',
      message: 'How often (in seconds) Alloy scrapes metrics.',
      isStaff: true
    },
    {
      element: '#proxy_url',
      action: 'type',
      value: 'http://proxy.example.com:8080',
      message: 'Optional HTTP proxy the ingest exporter routes through. Leave empty for a direct connection.',
      isStaff: true
    },
    {
      element: '#proxy_username',
      action: 'type',
      value: 'proxyuser',
      message: 'Username for an authenticating proxy (optional). Only used when a proxy URL is set.',
      isStaff: true
    },
    {
      element: '#proxy_password',
      action: 'type',
      value: 'proxypass',
      message: 'Password for the proxy username. Once saved it is never shown again; leave blank to keep the stored value.',
      isStaff: true
    }
  ];
}

// Logging.
export function alloyLoggingSteps() {
  return [
    {
      element: '#log_level',
      action: 'select',
      value: 'info',
      message: 'Set how verbose the Alloy agent logs are.'
    },
    {
      element: '#log_format',
      action: 'select',
      value: 'logfmt',
      message: 'Choose the log output format: logfmt or JSON.'
    }
  ];
}

// Self-Monitoring & Logs.
export function alloyLogSourcesSteps({ os = 'windows' } = {}) {
  const steps = [
    {
      element: 'label[for="self_monitoring_enabled"]',
      action: 'checkbox',
      value: true,
      message: "Enable Alloy self-monitoring to collect the agent's own health metrics."
    }
  ];

  if (os === 'linux') {
    steps.push(
      {
        element: 'label[for="journal_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Collect logs from the systemd journal.'
      },
      {
        element: '#journal_max_age',
        action: 'type',
        value: '12h',
        message: 'Only ship journal entries newer than this age.'
      }
    );
  } else {
    steps.push(
      {
        element: 'label[for="eventlog_system_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Collect the Windows System event log.'
      },
      {
        element: 'label[for="eventlog_application_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Collect the Windows Application event log.'
      },
      {
        element: '#eventlog_xpath_query',
        action: 'display',
        message: 'Filter which System-log events are shipped using this XPath query (defaults to errors and warnings).'
      }
    );
  }

  return steps;
}

// Monitoring Modules - one run covers every module for the given OS. Each
// module is enabled first (reveal), then its key fields are demonstrated.
export function alloyModulesSteps({ os = 'windows' } = {}) {
  const steps = [];

  if (os === 'linux') {
    // eBPF app monitoring (Beyla)
    steps.push(
      {
        element: 'label[for="ebpf_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Enable eBPF (Beyla) zero-touch app monitoring: traces and span metrics with no app changes.'
      },
      {
        element: '#ebpf_app_name',
        action: 'type',
        value: 'my-app',
        message: 'Name the application Beyla attributes telemetry to.'
      },
      {
        element: '#ebpf_open_ports',
        action: 'type',
        value: '8080,8443',
        message: 'List the ports your application listens on.'
      },
      {
        element: '#ebpf_routes_unmatched',
        action: 'select',
        value: 'path',
        message: 'Choose how unmatched HTTP routes are labelled.'
      }
    );
  } else {
    // IIS
    steps.push(
      {
        element: 'label[for="iis_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Enable IIS monitoring: a metrics collector plus a W3C access-log pipeline.'
      },
      {
        element: '#iis_site_include',
        action: 'type',
        value: '.*',
        message: 'Regex for which IIS sites to include.'
      },
      {
        element: 'label[for="iis_log_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Turn on the IIS access-log pipeline.'
      },
      {
        element: '#iis_log_path',
        action: 'display',
        message: 'Path Alloy tails for IIS access logs.'
      },
      // Hyper-V / Active Directory / DNS / DFSR
      {
        element: 'label[for="hyperv_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Enable Hyper-V metrics (requires the Hyper-V role on the host).'
      },
      {
        element: 'label[for="ad_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Enable Active Directory metrics (host must be a domain controller).'
      },
      {
        element: 'label[for="collector_dns_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Enable DNS Server metrics (requires the DNS Server role).'
      },
      {
        element: 'label[for="collector_dfsr_enabled"]',
        action: 'checkbox',
        value: true,
        message: 'Enable DFS Replication (DFSR) metrics.'
      }
    );
  }

  // Shared modules (both operating systems)
  steps.push(
    {
      element: 'label[for="apache_enabled"]',
      action: 'checkbox',
      value: true,
      message: 'Enable Apache monitoring (requires mod_status with ExtendedStatus On).'
    },
    {
      element: '#apache_status_port',
      action: 'type',
      value: '80',
      message: 'Port serving the Apache mod_status page.'
    },
    {
      element: 'label[for="mssql_enabled"]',
      action: 'checkbox',
      value: true,
      message: 'Enable SQL Server (MSSQL) monitoring.'
    },
    {
      element: '#mssql_connection_string',
      action: 'type',
      value: 'sqlserver://user:pass@host:1433',
      message: 'Connection string used to scrape SQL Server. Stored with the config.'
    },
    {
      element: 'label[for="postgres_enabled"]',
      action: 'checkbox',
      value: true,
      message: 'Enable PostgreSQL monitoring.'
    },
    {
      element: '#postgres_dsn',
      action: 'type',
      value: 'postgresql://user:pass@host:5432/db?sslmode=require',
      message: 'PostgreSQL DSN. The login needs the pg_monitor role.'
    },
    {
      element: 'label[for="vmware_enabled"]',
      action: 'checkbox',
      value: true,
      message: 'Enable VMware (vCenter / ESXi) monitoring.',
      isStaff: true
    },
    {
      element: '#vmware_endpoint',
      action: 'type',
      value: 'https://vcenter.example.com',
      message: 'vCenter / ESXi endpoint.',
      isStaff: true
    },
    {
      element: '#vmware_username',
      action: 'type',
      value: 'readonly@vsphere.local',
      message: 'Read-only vCenter / ESXi username.',
      isStaff: true
    },
    {
      element: '#vmware_password',
      action: 'type',
      value: 'Demo-Passw0rd',
      message: 'Password for the read-only account. Stored with the config.',
      isStaff: true
    }
  );

  return steps;
}

// Full Alloy walkthrough: intro + every section in form order.
export function alloyFullSteps({ isStaff = false, os = 'windows' } = {}) {
  return [
    ...alloyIntroSteps({ os }),
    ...(isStaff ? alloyOtlpSteps() : []),
    ...alloyLoggingSteps(),
    ...alloyLogSourcesSteps({ os }),
    ...alloyModulesSteps({ os })
  ];
}

// Section-scoped builders, keyed to the event emitted by each Alloy section.
export const ALLOY_SECTIONS = {
  otlp: () => alloyOtlpSteps(),
  logging: () => alloyLoggingSteps(),
  logsources: ({ os } = {}) => alloyLogSourcesSteps({ os }),
  modules: ({ os } = {}) => alloyModulesSteps({ os })
};
